import React, { useState, useEffect } from "react";
import { getGoogleClasses } from "../../utils/getGoogleClasses";
import { toast } from "react-toastify";

interface GoogleClass {
  id: string;
  name: string;
  description?: string;
  section?: string;
  added: boolean;
  existingClassId?: string;
}

interface SyncGoogleClassroomModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSync: (selectedClasses: GoogleClass[]) => void;
}

export default function SyncGoogleClassroomModal({
  isOpen,
  onClose,
  onSync,
}: SyncGoogleClassroomModalProps) {
  const [classes, setClasses] = useState<GoogleClass[]>([]);
  const [selectedClasses, setSelectedClasses] = useState<GoogleClass[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchGoogleClasses();
    }
  }, [isOpen]);

  const fetchGoogleClasses = async () => {
    setIsLoading(true);
    try {
      const result = await getGoogleClasses();
      if (result.error) {
        if (result.error.includes("403")) {
          setError(
            "Access denied. Please check your Google Classroom permissions or reconnect your account."
          );
        } else {
          toast.error(result.error);
          setError(result.error);
        }
      } else if (result.data) {
        setClasses(result.data);
      }
    } catch (error) {
      console.error("Failed to fetch Google Classroom classes:", error);
      toast.error(
        "Failed to fetch Google Classroom classes. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleClassToggle = (classItem: GoogleClass) => {
    if (classItem.added) return; // Don't allow selection of already added classes

    setSelectedClasses((prev) => {
      const isSelected = prev.some((c) => c.id === classItem.id);
      if (isSelected) {
        return prev.filter((c) => c.id !== classItem.id);
      } else {
        return [...prev, classItem];
      }
    });
  };

  const handleSync = async () => {
    if (selectedClasses.length === 0) {
      toast.error("Please select at least one class to sync");
      return;
    }

    setIsSyncing(true);
    try {
      await onSync(selectedClasses);
      // Close the modal after successful sync to show results in main component
      onClose();
    } catch (error) {
      toast.error("Failed to sync classes");
      console.error(error);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleSelectAll = () => {
    const availableClasses = classes.filter((c) => !c.added);
    setSelectedClasses(availableClasses);
  };

  const handleDeselectAll = () => {
    setSelectedClasses([]);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex justify-center items-center">
      <div className="bg-white w-full max-w-4xl rounded-lg shadow-lg overflow-hidden">
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-semibold">Sync with Google Classroom</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            disabled={isSyncing}
          >
            ✕
          </button>
        </div>

        <div className="p-6">
          {error ? (
            <div className="text-center py-8">
              <div className="mb-4">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Google Account Not Connected
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                You need to connect your Google account to sync with Google
                Classroom.
              </p>
              <button
                onClick={() => {
                  onClose();
                  // You can add navigation to settings here if needed
                }}
                className="btn btn-small mx-auto"
              >
                Go to Settings
              </button>
            </div>
          ) : isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-500">
                Loading Google Classroom classes...
              </p>
            </div>
          ) : (
            <>
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-3">
                  Select the classes you want to sync from Google Classroom.
                  Classes that are already added to your account are disabled.
                </p>

                {classes.filter((c) => !c.added).length > 0 && (
                  <div className="flex space-x-2 mb-4">
                    <button
                      onClick={handleSelectAll}
                      className="text-sm text-indigo-600 hover:text-indigo-700"
                    >
                      Select All
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      onClick={handleDeselectAll}
                      className="text-sm text-indigo-600 hover:text-indigo-700"
                    >
                      Deselect All
                    </button>
                  </div>
                )}
              </div>

              <div className="max-h-96 overflow-y-auto border rounded-lg">
                {classes.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>No classes found in Google Classroom</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {classes.map((classItem) => (
                      <div
                        key={classItem.id}
                        className={`p-4 flex items-center space-x-3 ${
                          classItem.added
                            ? "bg-gray-50 opacity-75"
                            : "hover:bg-gray-50"
                        }`}
                      >
                        <div className="flex items-center space-x-3 flex-1">
                          {!classItem.added ? (
                            <input
                              type="checkbox"
                              checked={selectedClasses.some(
                                (c) => c.id === classItem.id
                              )}
                              onChange={() => handleClassToggle(classItem)}
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                            />
                          ) : (
                            <div className="w-4 h-4 flex items-center justify-center">
                              <svg
                                className="w-4 h-4 text-green-500"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </div>
                          )}

                          <div className="flex-1">
                            <h4
                              className={`text-sm font-medium ${
                                classItem.added
                                  ? "text-gray-500"
                                  : "text-gray-900"
                              }`}
                            >
                              {classItem.name}
                            </h4>
                            {classItem.section && (
                              <p
                                className={`text-xs ${
                                  classItem.added
                                    ? "text-gray-400"
                                    : "text-gray-500"
                                }`}
                              >
                                Section: {classItem.section}
                              </p>
                            )}
                            {classItem.description && (
                              <p
                                className={`text-xs mt-1 ${
                                  classItem.added
                                    ? "text-gray-400"
                                    : "text-gray-500"
                                }`}
                              >
                                {classItem.description}
                              </p>
                            )}
                          </div>
                        </div>

                        {classItem.added && (
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-red-600 font-medium">
                              Already added
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-between items-center">
                <div className="text-sm text-gray-500">
                  {selectedClasses.length} class
                  {selectedClasses.length !== 1 ? "es" : ""} selected
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={onClose}
                    disabled={isSyncing}
                    className="btn btn-small btn-secondary disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSync}
                    disabled={isSyncing || selectedClasses.length === 0}
                    className="btn btn-small disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSyncing
                      ? "Syncing..."
                      : `Sync ${selectedClasses.length} Class${
                          selectedClasses.length !== 1 ? "es" : ""
                        }`}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
